import type { RequestContext } from "@mastra/core/request-context";

/**
 * Chat MCP connectors backed by Composio, which owns the OAuth grants, token
 * storage, and tool execution. The catalog covers the AIR-625 shortlist —
 * comms (Slack, Teams), mail/calendar (Gmail, Google Calendar, Outlook), docs
 * & storage (Google Docs/Drive, OneDrive, Dropbox, Box, Notion, Confluence),
 * work tracking (Linear, Jira, Asana, Monday, ClickUp), dev (GitHub, GitLab),
 * CRM & support (HubSpot, Salesforce, Zendesk, Intercom), meetings (Zoom),
 * finance (Stripe, Square, QuickBooks), and data (Airtable). Every connector
 * uses a Composio-MANAGED OAuth app — toolkits that would force us to run our
 * own OAuth app or collect API keys (Xero, Snowflake, Cloudflare, PayPal, …)
 * are deliberately absent.
 *
 * Single source of truth for connector identity: ids, the Composio toolkit
 * each id maps to, the curated tool allowlists, and the display names. Like
 * lib/chat/data-sources.ts this module is intentionally pure (the RequestContext
 * import is type-only) so it can be used from client components, server
 * actions, the chat API route, and prompt builders.
 *
 * Semantics: attachment is opt-in and fail-closed — an unknown or malformed
 * attach list resolves to empty. Attachment is a user preference, not a
 * security boundary: the chat route independently verifies each attached
 * connector has an ACTIVE Composio connected account for the requesting user
 * before any tool is injected (lib/connectors/toolsets.ts).
 */

export const CONNECTOR_IDS = [
  "slack",
  "gmail",
  "googleCalendar",
  "googleDocs",
  "googleDrive",
  "outlook",
  "oneDrive",
  "microsoftTeams",
  "dropbox",
  "box",
  "notion",
  "confluence",
  "linear",
  "jira",
  "asana",
  "monday",
  "clickup",
  "github",
  "gitlab",
  "hubspot",
  "salesforce",
  "zendesk",
  "intercom",
  "zoom",
  "stripe",
  "square",
  "quickbooks",
  "airtable",
] as const;

export type ConnectorId = (typeof CONNECTOR_IDS)[number];

export function isConnectorId(value: unknown): value is ConnectorId {
  return (CONNECTOR_IDS as readonly unknown[]).includes(value);
}

const NO_CONNECTORS: readonly ConnectorId[] = Object.freeze([]);

/**
 * The ONE normalization path for attach lists, shared by the request body, the
 * requestContext reads, and stored user preferences. Fail-closed: anything that
 * is not an array yields the empty list, unknown ids and duplicates are
 * dropped, and the result is frozen in canonical CONNECTOR_IDS order (stable
 * ordering keeps downstream cache keys deterministic).
 */
export function parseAttachedConnectors(input: unknown): readonly ConnectorId[] {
  if (!Array.isArray(input)) return NO_CONNECTORS;
  const requested = new Set(input.filter(isConnectorId));
  if (requested.size === 0) return NO_CONNECTORS;
  return Object.freeze(CONNECTOR_IDS.filter((id) => requested.has(id)));
}

/**
 * Strict counterpart to {@link parseAttachedConnectors} for values that are
 * about to be PERSISTED: the input must be an array of known connector ids
 * (duplicates tolerated), anything else is rejected (null) rather than
 * silently defaulted.
 */
export function sanitizeAttachedConnectors(input: unknown): ConnectorId[] | null {
  if (!Array.isArray(input) || !input.every(isConnectorId)) return null;
  return [...parseAttachedConnectors(input)];
}

/**
 * Product-level connection state of a connector for one user. Produced by the
 * Composio wrapper (./composio.ts maps connected-account statuses onto it;
 * "unavailable" = no auth config id in this environment) but declared here so
 * client components can name the union without touching the server-only
 * module.
 */
export type ConnectorConnectionStatus =
  | "connected"
  | "pending"
  | "needsReconnect"
  | "disconnected"
  | "unavailable";

/**
 * Display band per status for the connector pickers: connected first,
 * unavailable last, every other (connectable) state in between. Exhaustive
 * Record so adding a status forces an explicit display-order decision here.
 */
const DISPLAY_BAND: Record<ConnectorConnectionStatus, 0 | 1 | 2> = {
  connected: 0,
  pending: 1,
  needsReconnect: 1,
  disconnected: 1,
  unavailable: 2,
};

/**
 * Catalog ids in DISPLAY order for the connector pickers (/tools grid, chat
 * composer panel): connected first, then connectable, unavailable last —
 * registry order within each band. Statuses not yet known (map still loading,
 * fetch failed, entry missing) rank as connectable, so the list holds registry
 * order until real statuses arrive. Display-only: attach lists and cache keys
 * keep canonical CONNECTOR_IDS order via parseAttachedConnectors.
 */
export function sortConnectorIdsForDisplay(
  statuses:
    | Readonly<Partial<Record<ConnectorId, { status: ConnectorConnectionStatus }>>>
    | undefined,
): readonly ConnectorId[] {
  if (!statuses) return CONNECTOR_IDS;
  const bands: [ConnectorId[], ConnectorId[], ConnectorId[]] = [[], [], []];
  for (const id of CONNECTOR_IDS) {
    bands[DISPLAY_BAND[statuses[id]?.status ?? "disconnected"]].push(id);
  }
  return bands.flat();
}

/**
 * requestContext key holding the connectors VERIFIED active for this request
 * (attached ∩ ACTIVE connected accounts, set only after the toolset fetch
 * succeeded). The prompt builder keys off this, so the prompt never mentions a
 * connector whose tools are not in the toolset.
 */
const ACTIVE_CONNECTORS_CONTEXT_KEY = "activeConnectors";

export function setActiveConnectors(
  requestContext: RequestContext,
  connectorIds: readonly ConnectorId[],
): void {
  requestContext.set(ACTIVE_CONNECTORS_CONTEXT_KEY, connectorIds);
}

/**
 * Read the verified-active connectors off a requestContext. Absent context,
 * absent key, or a malformed stored value all resolve to the empty list —
 * Mastra resolves dynamic agent config with an empty context outside request
 * flows (tests, playground), and those see no connectors.
 */
export function getActiveConnectors(
  requestContext: RequestContext | undefined,
): readonly ConnectorId[] {
  return parseAttachedConnectors(requestContext?.get(ACTIVE_CONNECTORS_CONTEXT_KEY));
}

/** Composio toolkit slug per connector (uppercase, as Composio names them). */
export const CONNECTOR_TOOLKITS: Record<ConnectorId, string> = {
  slack: "SLACK",
  gmail: "GMAIL",
  googleCalendar: "GOOGLECALENDAR",
  googleDocs: "GOOGLEDOCS",
  googleDrive: "GOOGLEDRIVE",
  outlook: "OUTLOOK",
  oneDrive: "ONE_DRIVE",
  microsoftTeams: "MICROSOFT_TEAMS",
  dropbox: "DROPBOX",
  box: "BOX",
  notion: "NOTION",
  confluence: "CONFLUENCE",
  linear: "LINEAR",
  jira: "JIRA",
  asana: "ASANA",
  monday: "MONDAY",
  clickup: "CLICKUP",
  github: "GITHUB",
  gitlab: "GITLAB",
  hubspot: "HUBSPOT",
  salesforce: "SALESFORCE",
  zendesk: "ZENDESK",
  intercom: "INTERCOM",
  zoom: "ZOOM",
  stripe: "STRIPE",
  square: "SQUARE",
  quickbooks: "QUICKBOOKS",
  airtable: "AIRTABLE",
};

export function connectorIdForToolkitSlug(slug: string): ConnectorId | null {
  const upper = slug.toUpperCase();
  return CONNECTOR_IDS.find((id) => CONNECTOR_TOOLKITS[id] === upper) ?? null;
}

/**
 * Curated read-only tool allowlist, fetched from Composio by EXACT slug — never
 * by bare toolkit (Slack alone exposes 167 tools, GitHub 871; a handful of
 * schemas per connector keeps the prompt-token cost bounded however many
 * connectors are attached). Every slug was verified against the live Composio
 * catalog (scripts/composio-verify-tool-slugs.ts).
 */
export const CONNECTOR_READ_TOOLS: Record<ConnectorId, readonly string[]> = {
  slack: [
    "SLACK_LIST_ALL_CHANNELS",
    "SLACK_FIND_CHANNELS",
    "SLACK_FETCH_CONVERSATION_HISTORY",
    "SLACK_FETCH_MESSAGE_THREAD_FROM_A_CONVERSATION",
    "SLACK_FIND_USERS",
  ],
  gmail: [
    "GMAIL_FETCH_EMAILS",
    "GMAIL_LIST_THREADS",
    "GMAIL_FETCH_MESSAGE_BY_MESSAGE_ID",
    "GMAIL_GET_CONTACTS",
    "GMAIL_LIST_LABELS",
    "GMAIL_GET_PROFILE",
  ],
  googleCalendar: [
    "GOOGLECALENDAR_EVENTS_LIST",
    "GOOGLECALENDAR_FIND_EVENT",
    "GOOGLECALENDAR_LIST_CALENDARS",
    "GOOGLECALENDAR_FREE_BUSY_QUERY",
    "GOOGLECALENDAR_FIND_FREE_SLOTS",
  ],
  googleDocs: [
    "GOOGLEDOCS_SEARCH_DOCUMENTS",
    "GOOGLEDOCS_GET_DOCUMENT_BY_ID",
  ],
  googleDrive: [
    "GOOGLEDRIVE_FIND_FILE",
    "GOOGLEDRIVE_GET_FILE_METADATA",
    "GOOGLEDRIVE_DOWNLOAD_FILE",
    "GOOGLEDRIVE_EXPORT_GOOGLE_WORKSPACE_FILE",
    "GOOGLEDRIVE_LIST_SHARED_DRIVES",
    "GOOGLEDRIVE_LIST_COMMENTS",
  ],
  outlook: [
    "OUTLOOK_LIST_MESSAGES",
    "OUTLOOK_QUERY_EMAILS",
    "OUTLOOK_LIST_MAIL_FOLDERS",
    "OUTLOOK_GET_CALENDAR_VIEW",
    "OUTLOOK_GET_EVENT",
    "OUTLOOK_GET_USER_BY_EMAIL",
  ],
  oneDrive: [
    "ONE_DRIVE_SEARCH_DRIVE_ITEMS",
    "ONE_DRIVE_GET_ITEM",
    "ONE_DRIVE_LIST_FOLDER_CHILDREN",
    "ONE_DRIVE_DOWNLOAD_FILE",
    "ONE_DRIVE_LIST_DRIVES",
  ],
  microsoftTeams: [
    "MICROSOFT_TEAMS_SEARCH_MESSAGES",
    "MICROSOFT_TEAMS_TEAMS_LIST_CHANNELS",
    "MICROSOFT_TEAMS_TEAMS_LIST_CHANNEL_MESSAGES",
    "MICROSOFT_TEAMS_GET_CHAT_MESSAGE",
    "MICROSOFT_TEAMS_LIST_TEAM_MEMBERS",
    "MICROSOFT_TEAMS_LIST_MEETING_TRANSCRIPTS",
  ],
  dropbox: [
    "DROPBOX_FILES_SEARCH",
    "DROPBOX_LIST_FILES_IN_FOLDER",
    "DROPBOX_GET_METADATA",
    "DROPBOX_GET_TEMPORARY_LINK",
    "DROPBOX_LIST_RECEIVED_FILES",
  ],
  box: [
    "BOX_SEARCH_FOR_CONTENT",
    "BOX_GET_FILE_INFORMATION",
    "BOX_DOWNLOAD_FILE",
    "BOX_LIST_ITEMS_IN_FOLDER",
    "BOX_GET_FOLDER",
    "BOX_LIST_FILE_COMMENTS",
  ],
  notion: [
    "NOTION_SEARCH_NOTION_PAGE",
    "NOTION_GET_PAGE_MARKDOWN",
    "NOTION_QUERY_DATABASE",
    "NOTION_FETCH_DATABASE",
    "NOTION_FETCH_COMMENTS",
    "NOTION_LIST_USERS",
  ],
  confluence: [
    "CONFLUENCE_SEARCH_PAGES",
    "CONFLUENCE_GET_PAGE_BY_ID",
    "CONFLUENCE_GET_PAGES",
    "CONFLUENCE_SEARCH_SPACES",
    "CONFLUENCE_GET_CHILD_PAGES",
  ],
  linear: [
    "LINEAR_SEARCH_ISSUES",
    "LINEAR_GET_LINEAR_ISSUE",
    "LINEAR_LIST_LINEAR_ISSUES",
    "LINEAR_LIST_LINEAR_PROJECTS",
    "LINEAR_LIST_LINEAR_TEAMS",
    "LINEAR_LIST_COMMENTS",
  ],
  jira: [
    "JIRA_SEARCH_ISSUES",
    "JIRA_GET_ISSUE",
    "JIRA_LIST_ISSUE_COMMENTS",
    "JIRA_GET_ALL_PROJECTS",
    "JIRA_GET_TRANSITIONS",
  ],
  asana: [
    "ASANA_GET_TYPEAHEAD_OBJECTS",
    "ASANA_GET_A_TASK",
    "ASANA_GET_TASKS_FROM_A_PROJECT",
    "ASANA_GET_STORIES_FOR_TASK",
    "ASANA_GET_WORKSPACE_PROJECTS",
    "ASANA_GET_USERS_FOR_WORKSPACE",
  ],
  monday: [
    "MONDAY_BOARDS",
    "MONDAY_ITEMS_PAGE",
    "MONDAY_GET_ITEMS",
    "MONDAY_LIST_ITEMS_BY_COLUMN_VALUES",
    "MONDAY_COLUMNS",
    "MONDAY_LIST_USERS",
  ],
  clickup: [
    "CLICKUP_GET_FILTERED_TEAM_TASKS",
    "CLICKUP_GET_TASK",
    "CLICKUP_GET_TASK_COMMENTS",
    "CLICKUP_GET_AUTHORIZED_TEAMS_WORKSPACES",
    "CLICKUP_GET_SPACES",
    "CLICKUP_SEARCH_DOCS",
  ],
  github: [
    "GITHUB_SEARCH_ISSUES",
    "GITHUB_FIND_PULL_REQUESTS",
    "GITHUB_FIND_REPOSITORIES",
    "GITHUB_GET_A_PULL_REQUEST",
    "GITHUB_LIST_REPOSITORY_ISSUES",
    "GITHUB_GET_ISSUE_COMMENTS",
    "GITHUB_GET_REPOSITORY_CONTENT",
  ],
  gitlab: [
    "GITLAB_GET_PROJECTS",
    "GITLAB_LIST_PROJECT_ISSUES",
    "GITLAB_GET_ISSUE",
    "GITLAB_LIST_MERGE_REQUESTS",
    "GITLAB_GET_PROJECT_MERGE_REQUEST",
    "GITLAB_LIST_ISSUE_NOTES",
  ],
  hubspot: [
    "HUBSPOT_SEARCH_CRM_OBJECTS_BY_CRITERIA",
    "HUBSPOT_SEARCH_CONTACTS_BY_CRITERIA",
    "HUBSPOT_SEARCH_COMPANIES",
    "HUBSPOT_SEARCH_DEALS",
    "HUBSPOT_GET_COMPANY",
    "HUBSPOT_GET_DEAL",
    "HUBSPOT_LIST_CONTACTS",
  ],
  salesforce: [
    "SALESFORCE_RUN_SOQL_QUERY",
    "SALESFORCE_EXECUTE_SOSL_SEARCH",
    "SALESFORCE_GET_ACCOUNT",
    "SALESFORCE_GET_CONTACT",
    "SALESFORCE_GET_OPPORTUNITY",
    "SALESFORCE_GET_LEAD",
  ],
  zendesk: [
    "ZENDESK_SEARCH_ZENDESK",
    "ZENDESK_LIST_ZENDESK_TICKETS",
    "ZENDESK_GET_TICKET_COMMENTS",
    "ZENDESK_GET_USERS_REQUESTED_TICKETS",
    "ZENDESK_GET_USERS_ASSIGNED_TICKETS",
    "ZENDESK_LIST_ZENDESK_USERS",
  ],
  intercom: [
    "INTERCOM_SEARCH_CONVERSATIONS",
    "INTERCOM_GET_CONVERSATION",
    "INTERCOM_SEARCH_TICKETS",
    "INTERCOM_GET_TICKET",
    "INTERCOM_SEARCH_CONTACTS",
    "INTERCOM_LIST_CONTACTS",
  ],
  zoom: [
    "ZOOM_LIST_MEETINGS",
    "ZOOM_GET_A_MEETING",
    "ZOOM_LIST_ALL_RECORDINGS",
    "ZOOM_GET_MEETING_RECORDINGS",
    "ZOOM_GET_A_MEETING_SUMMARY",
    "ZOOM_SEARCH_COMPANY_CONTACTS",
  ],
  stripe: [
    "STRIPE_SEARCH_CUSTOMERS",
    "STRIPE_RETRIEVE_CUSTOMER",
    "STRIPE_RETRIEVE_CHARGE",
    "STRIPE_SEARCH_PAYMENT_INTENTS",
    "STRIPE_GET_INVOICES_INVOICE",
    "STRIPE_LIST_CUSTOMER_SUBSCRIPTIONS",
    "STRIPE_LIST_BALANCE_TRANSACTIONS",
  ],
  square: [
    "SQUARE_LIST_PAYMENTS",
    "SQUARE_SEARCH_CUSTOMERS",
    "SQUARE_RETRIEVE_CUSTOMER",
    "SQUARE_SEARCH_ORDERS",
    "SQUARE_LIST_LOCATIONS",
    "SQUARE_SEARCH_CATALOG_OBJECTS",
  ],
  quickbooks: [
    "QUICKBOOKS_QUERY_INVOICES",
    "QUICKBOOKS_QUERY_CUSTOMERS",
    "QUICKBOOKS_QUERY_PAYMENTS",
    "QUICKBOOKS_QUERY_BILLS",
    "QUICKBOOKS_QUERY_VENDORS",
    "QUICKBOOKS_GET_PROFIT_AND_LOSS_REPORT",
    "QUICKBOOKS_GET_BALANCE_SHEET_REPORT",
  ],
  airtable: [
    "AIRTABLE_LIST_BASES",
    "AIRTABLE_GET_BASE_SCHEMA",
    "AIRTABLE_LIST_RECORDS",
    "AIRTABLE_GET_RECORD",
  ],
};

/**
 * Write/side-effecting tools (send, create, modify, delete under the user's
 * identity). Registered only when CONNECTOR_WRITE_TOOLS_ENABLED is on (the
 * default in deploys — deploy.sh sets it true unless overridden). Money
 * movement (refunds, payouts, transfers) and container deletion are excluded
 * everywhere; the prompt instructs per-action confirmation.
 */
export const CONNECTOR_WRITE_TOOLS: Record<ConnectorId, readonly string[]> = {
  slack: ["SLACK_SEND_MESSAGE"],
  gmail: ["GMAIL_CREATE_EMAIL_DRAFT", "GMAIL_SEND_EMAIL", "GMAIL_REPLY_TO_THREAD"],
  googleCalendar: [
    "GOOGLECALENDAR_CREATE_EVENT",
    "GOOGLECALENDAR_PATCH_EVENT",
    "GOOGLECALENDAR_DELETE_EVENT",
  ],
  googleDocs: ["GOOGLEDOCS_CREATE_DOCUMENT_MARKDOWN", "GOOGLEDOCS_UPDATE_DOCUMENT_MARKDOWN"],
  googleDrive: [
    "GOOGLEDRIVE_CREATE_FILE",
    "GOOGLEDRIVE_CREATE_FOLDER",
    "GOOGLEDRIVE_UPLOAD_FILE",
    "GOOGLEDRIVE_MOVE_FILE",
    "GOOGLEDRIVE_CREATE_COMMENT",
  ],
  outlook: [
    "OUTLOOK_SEND_EMAIL",
    "OUTLOOK_REPLY_EMAIL",
    "OUTLOOK_FORWARD_MESSAGE",
    "OUTLOOK_CREATE_DRAFT",
    "OUTLOOK_CALENDAR_CREATE_EVENT",
  ],
  oneDrive: [
    "ONE_DRIVE_ONEDRIVE_CREATE_FOLDER",
    "ONE_DRIVE_ONEDRIVE_UPLOAD_FILE",
    "ONE_DRIVE_MOVE_ITEM",
    "ONE_DRIVE_COPY_ITEM",
  ],
  microsoftTeams: [
    "MICROSOFT_TEAMS_TEAMS_POST_CHANNEL_MESSAGE",
    "MICROSOFT_TEAMS_TEAMS_POST_CHAT_MESSAGE",
    "MICROSOFT_TEAMS_POST_MESSAGE_REPLY",
    "MICROSOFT_TEAMS_TEAMS_CREATE_CHAT",
    "MICROSOFT_TEAMS_CREATE_MEETING",
  ],
  dropbox: [
    "DROPBOX_UPLOAD_FILE",
    "DROPBOX_CREATE_FOLDER",
    "DROPBOX_MOVE_FILE_OR_FOLDER",
    "DROPBOX_COPY_FILE_OR_FOLDER",
    "DROPBOX_CREATE_PAPER_DOCUMENT",
  ],
  box: ["BOX_CREATE_FOLDER", "BOX_CREATE_COMMENT", "BOX_UPDATE_FILE", "BOX_UPDATE_FOLDER"],
  notion: [
    "NOTION_CREATE_NOTION_PAGE",
    "NOTION_ADD_MULTIPLE_PAGE_CONTENT",
    "NOTION_UPDATE_PAGE",
    "NOTION_CREATE_COMMENT",
    "NOTION_INSERT_ROW_DATABASE",
  ],
  confluence: [
    "CONFLUENCE_CREATE_PAGE",
    "CONFLUENCE_UPDATE_PAGE",
    "CONFLUENCE_CREATE_FOOTER_COMMENT",
  ],
  linear: [
    "LINEAR_CREATE_LINEAR_ISSUE",
    "LINEAR_UPDATE_ISSUE",
    "LINEAR_CREATE_PROJECT_UPDATE",
  ],
  jira: [
    "JIRA_CREATE_ISSUE",
    "JIRA_EDIT_ISSUE",
    "JIRA_ADD_COMMENT",
    "JIRA_TRANSITION_ISSUE",
    "JIRA_ASSIGN_ISSUE",
  ],
  asana: [
    "ASANA_CREATE_SUBTASK",
    "ASANA_CREATE_TASK_COMMENT",
    "ASANA_CREATE_PROJECT_STATUS_UPDATE",
    "ASANA_ADD_TASK_TO_SECTION",
  ],
  monday: [
    "MONDAY_CREATE_ITEM",
    "MONDAY_CREATE_ITEM_FROM_NL",
    "MONDAY_CHANGE_SIMPLE_COLUMN_VALUE",
    "MONDAY_MOVE_ITEM_TO_GROUP",
  ],
  clickup: [
    "CLICKUP_CREATE_TASK",
    "CLICKUP_UPDATE_TASK",
    "CLICKUP_CREATE_TASK_COMMENT",
    "CLICKUP_CREATE_DOC",
  ],
  github: [
    "GITHUB_CREATE_AN_ISSUE",
    "GITHUB_CREATE_AN_ISSUE_COMMENT",
    "GITHUB_CREATE_A_PULL_REQUEST",
    "GITHUB_ADD_LABELS_TO_AN_ISSUE",
  ],
  gitlab: [
    "GITLAB_CREATE_PROJECT_ISSUE",
    "GITLAB_CREATE_ISSUE_NOTE",
    "GITLAB_CREATE_MERGE_REQUEST",
    "GITLAB_UPDATE_PROJECT_ISSUE",
  ],
  hubspot: [
    "HUBSPOT_CREATE_CONTACT_FROM_NL",
    "HUBSPOT_CREATE_DEAL_FROM_NL",
    "HUBSPOT_CREATE_CRM_OBJECT_FROM_NL",
    "HUBSPOT_CREATE_NOTE",
    "HUBSPOT_CREATE_MEETING",
  ],
  salesforce: [
    "SALESFORCE_CREATE_CONTACT",
    "SALESFORCE_CREATE_ACCOUNT",
    "SALESFORCE_CREATE_OPPORTUNITY",
    "SALESFORCE_CREATE_LEAD",
    "SALESFORCE_UPDATE_OPPORTUNITY",
  ],
  zendesk: ["ZENDESK_CREATE_ZENDESK_TICKET"],
  intercom: [
    "INTERCOM_CREATE_TICKET",
    "INTERCOM_REPLY_TO_CONVERSATION",
    "INTERCOM_REPLY_TICKET",
    "INTERCOM_CREATE_CONTACT",
    "INTERCOM_UPDATE_TICKET",
  ],
  zoom: ["ZOOM_CREATE_A_MEETING"],
  stripe: [
    "STRIPE_CREATE_CUSTOMER",
    "STRIPE_CREATE_INVOICE",
    "STRIPE_CREATE_PAYMENT_LINK",
    "STRIPE_CREATE_PRODUCT",
    "STRIPE_CREATE_PRICE",
  ],
  square: [
    "SQUARE_CREATE_CUSTOMER",
    "SQUARE_UPDATE_CUSTOMER",
    "SQUARE_CREATE_ORDER",
    "SQUARE_UPSERT_CATALOG_OBJECT",
  ],
  quickbooks: [
    "QUICKBOOKS_CREATE_INVOICE",
    "QUICKBOOKS_CREATE_CUSTOMER",
    "QUICKBOOKS_CREATE_ESTIMATE",
    "QUICKBOOKS_CREATE_BILL",
  ],
  airtable: [
    "AIRTABLE_CREATE_RECORDS",
    "AIRTABLE_CREATE_RECORD_FROM_NATURAL_LANGUAGE",
    "AIRTABLE_UPDATE_RECORD",
    "AIRTABLE_CREATE_COMMENT",
  ],
};

export function connectorToolAllowlist(
  connectorIds: readonly ConnectorId[],
  includeWrites: boolean,
): string[] {
  return connectorIds.flatMap((id) => [
    ...CONNECTOR_READ_TOOLS[id],
    ...(includeWrites ? CONNECTOR_WRITE_TOOLS[id] : []),
  ]);
}

/**
 * Map a Composio tool slug (e.g. "GMAIL_FETCH_EMAILS") back to its connector
 * via the `<TOOLKIT>_` prefix. Covers slugs beyond the curated allowlists so
 * display fallbacks keep working if the allowlist grows.
 */
export function connectorIdForToolSlug(toolSlug: string): ConnectorId | null {
  return CONNECTOR_IDS.find((id) => toolSlug.startsWith(`${CONNECTOR_TOOLKITS[id]}_`)) ?? null;
}

/** Brand names are trademarks — rendered verbatim, never translated. */
export const CONNECTOR_META: Record<ConnectorId, { name: string }> = {
  slack: { name: "Slack" },
  gmail: { name: "Gmail" },
  googleCalendar: { name: "Google Calendar" },
  googleDocs: { name: "Google Docs" },
  googleDrive: { name: "Google Drive" },
  outlook: { name: "Outlook" },
  oneDrive: { name: "OneDrive" },
  microsoftTeams: { name: "Microsoft Teams" },
  dropbox: { name: "Dropbox" },
  box: { name: "Box" },
  notion: { name: "Notion" },
  confluence: { name: "Confluence" },
  linear: { name: "Linear" },
  jira: { name: "Jira" },
  asana: { name: "Asana" },
  monday: { name: "monday.com" },
  clickup: { name: "ClickUp" },
  github: { name: "GitHub" },
  gitlab: { name: "GitLab" },
  hubspot: { name: "HubSpot" },
  salesforce: { name: "Salesforce" },
  zendesk: { name: "Zendesk" },
  intercom: { name: "Intercom" },
  zoom: { name: "Zoom" },
  stripe: { name: "Stripe" },
  square: { name: "Square" },
  quickbooks: { name: "QuickBooks" },
  airtable: { name: "Airtable" },
};
